/**
 * The arithmetic behind `TimePicker` — a wall clock, its dial geometry, and the
 * `HH:MM` string that is the only thing anybody outside the component sees.
 *
 * Separate from the component on purpose. The **emitted value is the contract**:
 * availability rules store a bare `HH:MM` local wall clock (`opens_at` /
 * `closes_at`, deliberately not a timestamp — see `AvailabilityStep`), and the
 * organizer's fields join the same `HH:MM` onto a date before `toInstant` turns
 * it into the UTC instant the API stores. A rounding error in here is an event
 * an hour wrong, silently, so it is tested as arithmetic rather than through a
 * dial nobody can assert against.
 *
 * ⚠️ **24-hour on the wire, 12-hour on the face.** The dial shows 1–12 with an
 * AM/PM pair because that is what the product owner asked to look at; every
 * value that leaves here is 24-hour. The two midnight/noon crossings are where
 * a hand-rolled conversion goes wrong — 12 AM is hour 0 and 12 PM is hour 12,
 * neither of which follows from `hour % 12` — so both directions live here and
 * are covered by name.
 */

/** A wall clock with no date and no zone. `hour` is 0–23. */
export type Clock = { hour: number; minute: number }

/** Which half of the dial's two-stage flow is being edited. */
export type ClockStage = 'hour' | 'minute'

/** Before noon or after it. Rendered ص/م in Arabic, AM/PM in English. */
export type Meridiem = 'am' | 'pm'

/** `09:00`, `18:45` — 24-hour, zero-padded, exactly what goes on the wire. */
export function toClockString({ hour, minute }: Clock): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

/**
 * The inverse, refusing anything that is not a real time.
 *
 * ⚠️ **Returns `null` rather than a default.** A picker that silently reads an
 * unparseable stored value as midnight would show 12:00 AM over a field holding
 * something else, and the next save would write the lie back. The caller opens
 * on a stated fallback instead, so "nothing chosen" stays visible as nothing.
 */
export function parseClock(value: string | null | undefined): Clock | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(value ?? ''))
  if (!match) return null
  const hour = Number(match[1])
  const minute = Number(match[2])
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null
  return { hour, minute }
}

/**
 * 24-hour → what the face shows.
 *
 * ⚠️ The `|| 12` is the midnight/noon case, not defensive padding: `0 % 12` and
 * `12 % 12` are both 0, and there is no 0 on a clock face. Hour 0 is 12 AM and
 * hour 12 is 12 PM.
 */
export function toDisplay(hour: number): { hour12: number; meridiem: Meridiem } {
  return { hour12: hour % 12 || 12, meridiem: hour < 12 ? 'am' : 'pm' }
}

/** The inverse. 12 AM → 0, 12 PM → 12, 1 PM → 13. */
export function fromDisplay(hour12: number, meridiem: Meridiem): number {
  const base = hour12 % 12
  return meridiem === 'pm' ? base + 12 : base
}

/**
 * Where a value sits on the face, in degrees clockwise from 12 o'clock.
 *
 * ⚠️ **Always clockwise, in both directions.** The dial is not mirrored under
 * RTL: an analogue clock runs clockwise in Cairo exactly as it does in London,
 * and flipping it because the page reads right-to-left would produce a clock
 * that runs backwards. This is the one place in the codebase where the inline
 * axis is deliberately not the reading axis — the surrounding layout still
 * mirrors, the face does not.
 */
export function angleFor(value: number, unitsPerTurn: number): number {
  return (value % unitsPerTurn) * (360 / unitsPerTurn)
}

/** The point a hand's tip lands on, as a fraction of the face's box. */
export function handTip(degrees: number, radius: number): { x: number; y: number } {
  const radians = ((degrees - 90) * Math.PI) / 180
  return { x: 50 + radius * Math.cos(radians), y: 50 + radius * Math.sin(radians) }
}

/**
 * A pointer position on the face → the value under it.
 *
 * `x`/`y` are relative to the face's centre. The `+ 360` is because `atan2`
 * hands back negatives for the left half of the dial, which would floor to a
 * negative index on every hour between 7 and 11.
 */
export function valueAtPoint(x: number, y: number, unitsPerTurn: number): number {
  const degrees = (Math.atan2(y, x) * 180) / Math.PI + 90
  const turn = ((degrees % 360) + 360) % 360
  return Math.round(turn / (360 / unitsPerTurn)) % unitsPerTurn
}

/**
 * Move by `delta` units and wrap.
 *
 * The wrap is what makes the arrow keys usable: holding Left off 12:00 has to
 * reach 11:55, not stop dead at the top of the dial.
 */
export function wrap(value: number, delta: number, modulus: number): number {
  return (((value + delta) % modulus) + modulus) % modulus
}

/**
 * Every minute the dial can be clicked to, and the one the value already holds.
 *
 * ⚠️ **The face is marked in fives; the value is not restricted to them.** The
 * dropdown this replaces stepped in 30 minutes, so a tier stored at 18:45 could
 * be displayed but never entered — the product owner's complaint. Clicking lands
 * on a 5-minute mark, the arrow keys move by 1, and a held off-mark value is
 * merged in so editing a record never rounds it away.
 */
export function minuteMarks(step: number, held?: number): number[] {
  const marks: number[] = []
  for (let minute = 0; minute < 60; minute += step) marks.push(minute)
  if (held !== undefined && !marks.includes(held)) {
    marks.push(held)
    marks.sort((a, b) => a - b)
  }
  return marks
}
